import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaTag } from 'react-icons/fa';
import PageMeta from './PageMeta';

const blogPosts = [
  {
    id: 1,
    title: 'The Importance of Regular Dental Check-ups',
    excerpt: 'Discover why scheduling regular dental visits is crucial for maintaining optimal oral health and preventing serious issues.',
    content: 'Regular dental check-ups are essential for maintaining good oral health. During these visits, your dentist can detect early signs of problems such as cavities, gum disease, and oral cancer. Early detection often means simpler and less expensive treatments. Additionally, professional cleanings remove plaque and tartar that regular brushing and flossing might miss. Remember, prevention is always better than cure when it comes to dental health.',
    author: 'Dr. Sarah Johnson',
    date: '2023-06-15',
    category: 'Preventive Care',
    image: 'https://via.placeholder.com/400x250?text=Dental+Check-up',
    readTime: '3 min read',
  },
  {
    id: 2,
    title: 'Smile Makeover: What to Expect',
    excerpt: 'Learn about the process of getting a smile makeover and how it can boost your confidence and oral health.',
    content: 'A smile makeover is a comprehensive approach to improving the appearance of your smile. It may involve several procedures such as teeth whitening, veneers, crowns, or orthodontic treatments. The process typically begins with a consultation where your dentist assesses your oral health and discusses your goals. Treatment plans are customized to each patient\'s needs and may take several visits to complete. The result is not just a beautiful smile, but also improved oral function and health.',
    author: 'Dr. Michael Chen',
    date: '2023-06-10',
    category: 'Cosmetic Dentistry',
    image: 'https://via.placeholder.com/400x250?text=Smile+Makeover',
    readTime: '4 min read',
  },
  {
    id: 3,
    title: 'Dental Care Tips for Children',
    excerpt: 'Help your kids develop good oral hygiene habits from an early age with these expert tips.',
    content: 'Teaching children good dental habits early can set them up for a lifetime of healthy smiles. Start by introducing toothbrushing as soon as the first tooth appears, using a soft-bristled brush and a pea-sized amount of fluoride toothpaste. Make it fun by letting them choose their own toothbrush and playing games during brushing time. Regular dental visits should begin by age one, and parents should supervise brushing until children are around 8 years old. Remember, leading by example is one of the best ways to encourage good habits.',
    author: 'Dr. Emily Davis',
    date: '2023-06-05',
    category: 'Pediatric Dentistry',
    image: 'https://via.placeholder.com/400x250?text=Kids+Dental+Care',
    readTime: '5 min read',
  },
  {
    id: 4,
    title: 'The Latest in Dental Technology',
    excerpt: 'Explore cutting-edge technologies that are revolutionizing dental care and improving patient experiences.',
    content: 'Dental technology has advanced significantly in recent years, making treatments more efficient, comfortable, and effective. Digital X-rays provide clearer images with less radiation exposure. Laser dentistry allows for precise treatments with minimal discomfort and faster healing. CAD/CAM technology enables same-day crowns and restorations. Intraoral cameras let patients see what their dentist sees, improving communication and understanding. These innovations not only improve treatment outcomes but also enhance the overall patient experience.',
    author: 'Dr. Sarah Johnson',
    date: '2023-05-30',
    category: 'Technology',
    image: 'https://via.placeholder.com/400x250?text=Dental+Technology',
    readTime: '4 min read',
  },
];

const categories = ['All', 'Preventive Care', 'Cosmetic Dentistry', 'Pediatric Dentistry', 'Technology'];

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPost, setSelectedPost] = useState(null);

  const filteredPosts = selectedCategory === 'All'
    ? blogPosts
    : blogPosts.filter(post => post.category === selectedCategory);

  const openPost = (post) => {
    setSelectedPost(post);
  };

  const closePost = () => {
    setSelectedPost(null);
  };

  return (
    <div className="bg-phoenix-beige min-h-screen">
      <PageMeta
        title="Blog & Dental Tips | Phoenix Dental"
        description="Read our latest articles on oral hygiene, dental health tips, and clinic news."
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-phoenix-beige via-phoenix-white to-phoenix-beige py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            className="text-5xl lg:text-6xl font-bold text-phoenix-black mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Dental <span className="text-phoenix-gold">Blog</span>
          </motion.h1>
          <motion.p
            className="text-xl text-phoenix-gray max-w-3xl mx-auto mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Stay informed with the latest dental tips, news, and educational content from our expert team.
            Your guide to better oral health and beautiful smiles.
          </motion.p>
        </div>
      </section>

      {/* Filter Buttons */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {categories.map((category) => (
              <motion.button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category
                    ? 'bg-phoenix-gold text-phoenix-white'
                    : 'bg-phoenix-white text-phoenix-black hover:bg-phoenix-gold hover:text-phoenix-white'
                  }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {category}
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            layout
          >
            {filteredPosts.map((post) => (
              <motion.article
                key={post.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-phoenix-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => openPost(post)}
                whileHover={{ scale: 1.02 }}
              >
                <img src={post.image} alt={post.title} className="w-full h-48 object-cover" />
                <div className="p-6">
                  <div className="flex items-center text-sm text-phoenix-gray mb-2">
                    <FaCalendarAlt className="mr-1" />
                    <span className="mr-4">{new Date(post.date).toLocaleDateString()}</span>
                    <FaUser className="mr-1" />
                    <span className="mr-4">{post.author}</span>
                    <FaTag className="mr-1" />
                    <span>{post.category}</span>
                  </div>
                  <h2 className="text-xl font-semibold text-phoenix-black mb-2">{post.title}</h2>
                  <p className="text-phoenix-gray mb-4">{post.excerpt}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-phoenix-gold">{post.readTime}</span>
                    <span className="text-phoenix-gold font-medium">Read More →</span>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Full Post Modal */}
      {selectedPost && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closePost}
        >
          <motion.div
            className="bg-phoenix-white rounded-lg max-w-4xl w-full max-h-full overflow-y-auto"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <button
                onClick={closePost}
                className="float-right text-phoenix-gray hover:text-phoenix-black text-2xl"
              >
                ×
              </button>
              <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover rounded-lg mb-6" />
              <div className="flex items-center text-sm text-phoenix-gray mb-4">
                <FaCalendarAlt className="mr-1" />
                <span className="mr-4">{new Date(selectedPost.date).toLocaleDateString()}</span>
                <FaUser className="mr-1" />
                <span className="mr-4">{selectedPost.author}</span>
                <FaTag className="mr-1" />
                <span>{selectedPost.category}</span>
              </div>
              <h1 className="text-3xl font-bold text-phoenix-black mb-4">{selectedPost.title}</h1>
              <div className="prose max-w-none text-phoenix-gray">
                {selectedPost.content.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Blog;
